/**
 * StatusStrip — always-visible row of chips for level, streak, claimable
 * rewards and customization shop. Mounted on the dashboard, just under the
 * welcome banner. Designed to NOT overlap the bottom nav.
 */
import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Trophy, Gift, Sparkles } from "lucide-react";
import { usePoints } from "@/hooks/usePoints";
import { usePendingRewards } from "@/hooks/usePendingRewards";
import { ClaimDrawer } from "./ClaimDrawer";
import { cn } from "@/lib/utils";

interface ChipProps {
  onClick: () => void;
  icon: React.ReactNode;
  label: string;
  pulse?: boolean;
  ariaLabel: string;
  variant?: "default" | "claim-active" | "muted" | "redeem";
  title?: string;
  badgeCount?: number;
}

function Chip({ onClick, icon, label, pulse, ariaLabel, variant = "default", title, badgeCount }: ChipProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      title={title}
      className={cn(
        "relative flex items-center justify-center gap-1.5 px-3 py-2 rounded-full border backdrop-blur-md",
        "shadow-[inset_0_1px_0_hsl(0_0%_100%/0.18)] active:scale-[0.97] transition-all duration-150 ease-out",
        "min-w-0 whitespace-nowrap text-[12px] sm:text-sm font-semibold flex-1 min-h-[42px]",
        variant === "default" && "bg-card/80 hover:bg-accent/50",
        variant === "muted" && "bg-muted/60 text-muted-foreground hover:bg-muted",
        variant === "claim-active" && [
          "border-primary/50 text-primary-foreground",
          "bg-gradient-to-r from-primary to-primary-glow",
          "shadow-[0_8px_24px_-8px_hsl(var(--primary)/0.7)] hover:brightness-105",
        ],
        variant === "redeem" && [
          "border-emerald/40 text-emerald-foreground",
          "bg-emerald hover:brightness-110",
        ],
        pulse && "ring-2 ring-primary/50 animate-pulse"
      )}
      style={{ touchAction: "manipulation" }}
    >
      <span
        className={cn(
          "flex items-center shrink-0",
          variant === "claim-active" || variant === "redeem" ? "text-current" : "text-primary"
        )}
      >
        {icon}
      </span>
      <span className="truncate">{label}</span>
      {badgeCount && badgeCount > 0 ? (
        <span className="ml-0.5 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 rounded-full bg-white text-amber-600 text-[10px] font-bold shadow-sm">
          {badgeCount}
        </span>
      ) : null}
    </button>
  );
}

export function StatusStrip() {
  const navigate = useNavigate();
  const { points, loading } = usePoints();
  const { count: claimCount } = usePendingRewards();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [floatDelta, setFloatDelta] = useState<number | null>(null);
  const [displayPoints, setDisplayPoints] = useState<number | null>(null);
  const prevPointsRef = useRef<number | null>(null);

  // Animate a "+N" floater and roll the total up whenever points tick up.
  useEffect(() => {
    if (loading) return;
    const prev = prevPointsRef.current;
    prevPointsRef.current = points;
    if (prev !== null && points > prev) {
      setFloatDelta(points - prev);
      const start = performance.now();
      const dur = 700;
      let raf = 0;
      const step = (t: number) => {
        const p = Math.min(1, (t - start) / dur);
        setDisplayPoints(Math.round(prev + (points - prev) * (1 - Math.pow(1 - p, 3))));
        if (p < 1) raf = requestAnimationFrame(step);
      };
      raf = requestAnimationFrame(step);
      const t = setTimeout(() => setFloatDelta(null), 1600);
      return () => { clearTimeout(t); cancelAnimationFrame(raf); };
    }
    setDisplayPoints(points);
  }, [points, loading]);

  if (loading) return null;
  const shown = displayPoints ?? points;

  return (
    <>
      <div aria-label="Your points, claim and redeem">
        <div className="flex items-stretch gap-2 w-full">
          <div className="relative flex-1 flex">
            <Chip
              onClick={() => navigate("/my-progress")}
              icon={<Trophy className="h-4 w-4" />}
              label={`${points} pts`}
              ariaLabel={`${points} points. Open My Journey.`}
            />
            {floatDelta !== null && (
              <span
                aria-hidden
                className="absolute -top-2 right-1 text-[11px] font-bold text-amber-600 dark:text-amber-300 pointer-events-none"
                style={{ animation: 'floatUp 1.6s ease-out forwards' }}
              >
                +{floatDelta}
              </span>
            )}
          </div>

          <Chip
            onClick={() => setDrawerOpen(true)}
            icon={<Gift className="h-4 w-4" />}
            label={claimCount > 0 ? `Claim ${claimCount}` : "Claim"}
            pulse={claimCount > 0}
            variant={claimCount > 0 ? "claim-active" : "muted"}
            ariaLabel={
              claimCount > 0
                ? `${claimCount} reward${claimCount === 1 ? "" : "s"} ready to claim`
                : "No rewards to claim yet — complete a goal to earn one"
            }
            title={
              claimCount > 0
                ? `Tap to claim ${claimCount} reward${claimCount === 1 ? "" : "s"}`
                : "Complete a goal or earn a certificate to unlock a reward"
            }
          />

          <Chip
            onClick={() => navigate("/rewards")}
            icon={<Sparkles className="h-4 w-4" />}
            label="Redeem"
            variant="redeem"
            ariaLabel="Open the rewards hub to redeem points"
            title="Spend your points on rewards"
          />
        </div>
      </div>

      <ClaimDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </>
  );
}
